import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import AdministratorDashboard from './AdministratorDashboard';
import InvestorDashboard from './InvestorDashboard';
import SocialActorDashboard from './SocialActorDashboard';
import ProjectManagerDashboard from './ProjectManagerDashboard';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Activity, 
  BookOpen, 
  FileText,
  Heart, 
  Leaf, 
  BarChart3, 
  Clock,
  Target,
  AlertCircle,
  CheckCircle2,
  LogOut,
  KeyRound,
  Wallet,
  Menu,
  Building2,
  Briefcase,
  X,
  ClipboardList,
  Shuffle,
  Layers,
  GraduationCap
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Route to role-specific dashboard
  const renderDashboard = () => {
    switch (user.role) {
      case 'administrator':
        return <AdministratorDashboard />;
      case 'investor':
        return <InvestorDashboard />;
      case 'social_actor':
        return <SocialActorDashboard />;
      case 'project_manager':
        return <ProjectManagerDashboard />;
      case 'guest':
        return <SocialActorDashboard />;
      default:
        return <AdministratorDashboard />;
    }
  };

  const isGuest = user.role === 'guest';

  const getRoleDisplay = (role) => {
    return t(`roles.${role}`, role);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 lg:translate-x-0 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <img 
              src="/assets/isisp_logo.png" 
              alt="ISISP Logo" 
              className="h-16 w-auto"
            />
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Close sidebar"
              data-testid="sidebar-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <h2 className="text-xl font-bold text-gray-800">ISISP Platform</h2>
        <p className="text-sm text-gray-600 mt-1">{t('nav.platform_subtitle')}</p>
        </div>

        <nav className="flex-1 overflow-y-auto mt-4 px-4 pb-4">
          <div className="space-y-2">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 font-medium"
              data-testid="dashboard-nav-link"
            >
              <BarChart3 className="h-5 w-5" />
              <span>{t('nav.dashboard')}</span>
            </Link>
            {!isGuest && (
              <>
                <Link 
                  to="/projects" 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  data-testid="projects-nav-link"
                >
                  <Activity className="h-5 w-5" />
                  <span>{t('nav.projects')}</span>
                </Link>
                <Link 
                  to="/templates" 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  data-testid="templates-nav-link"
                >
                  <BookOpen className="h-5 w-5" />
                  <span>{t('nav.templates')}</span>
                </Link>
                <Link 
                  to="/social-actor-registry" 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Building2 className="h-5 w-5" />
                  <span>{t('nav.social_actor_registry')}</span>
                </Link>
                <Link 
                  to="/social-investors" 
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <Briefcase className="h-5 w-5" />
                  <span>{t('nav.social_investor_registry')}</span>
                </Link>
              </>
            )}

            <div className="pt-3 mt-3 border-t border-gray-200">
              <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('nav.modules')}</p>
              {(user.role === 'investor' || user.role === 'administrator' || user.role === 'project_manager') && (
                <Link
                  to="/investor/portfolio"
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                  data-testid="investor-portfolio-nav-link"
                >
                  <Wallet className="h-5 w-5" />
                  <span>My Investments</span>
                </Link>
              )}
              <Link 
                to="/social-investment-registration" 
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                data-testid="social-investment-registration-nav-link"
              >
                <ClipboardList className="h-5 w-5" />
                <span>{t('nav.social_investment_registration')}</span>
              </Link>
              <Link 
                to="/participatory-planning" 
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                data-testid="participatory-planning-nav-link"
              >
                <Users className="h-5 w-5" />
                <span>{t('nav.participatory_planning')}</span>
              </Link>
              {!isGuest && (
                <>
                  <Link 
                    to="/combined-investments" 
                    className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    data-testid="combined-investments-nav-link"
                  >
                    <Layers className="h-5 w-5" />
                    <span>{t('nav.combined_investments')}</span>
                  </Link>
                  <Link 
                    to="/supply-demand-matching" 
                    className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    data-testid="supply-demand-matching-nav-link"
                  >
                    <Shuffle className="h-5 w-5" />
                    <span>{t('nav.supply_demand_matching')}</span>
                  </Link>
                </>
              )}
              <Link 
                to="/capacity-building" 
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                data-testid="capacity-building-nav-link"
              >
                <GraduationCap className="h-5 w-5" />
                <span>{t('nav.capacity_building')}</span>
              </Link>
              {!isGuest && (
                <>
                  <Link 
                    to="/investment-evaluation" 
                    className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    data-testid="investment-evaluation-nav-link"
                  >
                    <Target className="h-5 w-5" />
                    <span>{t('nav.investment_evaluation')}</span>
                  </Link>
                  <Link 
                    to="/impact-dashboard" 
                    className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    data-testid="impact-dashboard-nav-link"
                  >
                    <BarChart3 className="h-5 w-5" />
                    <span>{t('nav.impact_dashboard')}</span>
                  </Link>
                  <Link 
                    to="/qa-overview" 
                    className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    data-testid="qa-overview-nav-link"
                  >
                    <ClipboardList className="h-5 w-5" />
                    <span>{t('nav.qa_overview')}</span>
                  </Link>
                  <Link 
                    to="/certification-renewal" 
                    className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                    data-testid="certification-renewal-nav-link"
                  >
                    <Target className="h-5 w-5" />
                    <span>{t('nav.certification_renewal')}</span>
                  </Link>
                  {(user.role === 'administrator' || user.role === 'project_manager') && (
                    <Link 
                      to="/continuous-improvement" 
                      className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                      data-testid="continuous-improvement-nav-link"
                    >
                      <TrendingUp className="h-5 w-5" />
                      <span>{t('nav.continuous_improvement')}</span>
                    </Link>
                  )}
                </>
              )}
            </div>
            <Link 
              to="/guides" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              data-testid="guides-nav-link"
            >
              <FileText className="h-5 w-5" />
              <span>{t('nav.user_guides')}</span>
            </Link>
          </div>
        </nav>

        <div className="flex-shrink-0 p-4 border-t">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{user.username}</p>
                <p className="text-sm text-gray-500">{getRoleDisplay(user.role)}</p>
                {user.organization_name && (
                  <p className="text-xs text-gray-500">{user.organization_name}</p>
                )}
              </div>
            </div>
            <Link
              to="/change-password"
              className="w-full inline-flex items-center justify-center gap-2 h-9 px-3 mb-2 text-xs font-medium rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
              data-testid="change-password-link"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {t('common.change_password', 'Change password')}
            </Link>
            <Button 
              onClick={onLogout} 
              variant="outline" 
              size="sm" 
              className="w-full"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                aria-label="Open sidebar menu"
                data-testid="sidebar-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
                <p className="text-sm text-gray-600">{t('common.welcome_back', { name: user.username })}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <LanguageSwitcher />
              <NotificationBell user={user} />
            </div>
          </div>
        </header>

        <main className="p-6">
          {renderDashboard()}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;